/**
 * 설정은 본문을 갈아끼우지 않는다.
 * 고정 높이 모달이다. 왼쪽은 대상별 항목, 오른쪽은 그 항목이다.
 * 앱과 워크스페이스는 「설정」 창이다. 플랜은 플랜이 열려 있을 때만 같은 껍질의 별도 창이다.
 */
import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import {
  Board20Filled,
  Board20Regular,
  Building20Filled,
  Building20Regular,
  Calendar20Filled,
  Calendar20Regular,
  CalendarLtr20Filled,
  CalendarLtr20Regular,
  Color20Filled,
  Color20Regular,
  DocumentBulletList20Filled,
  DocumentBulletList20Regular,
  Eye20Filled,
  Eye20Regular,
  History20Filled,
  History20Regular,
  Keyboard20Filled,
  Keyboard20Regular,
  Key20Filled,
  Key20Regular,
  People20Filled,
  People20Regular,
  Person20Filled,
  Person20Regular,
  PersonAdd20Filled,
  PersonAdd20Regular,
  PlugConnected20Filled,
  PlugConnected20Regular,
  Settings20Filled,
  Settings20Regular,
  Share20Filled,
  Share20Regular,
  Shield20Filled,
  Shield20Regular,
  Tag20Filled,
  Tag20Regular,
  Warning20Filled,
  Warning20Regular,
  type FluentIcon,
} from "@fluentui/react-icons";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { AppDialog } from "@/components/app-dialog";
import { PageHeaderTitle } from "@/components/ui-typography";
import { McpConnectionView } from "@/features/mcp-settings/McpConnectionView";
import { SystemLogsView } from "@/features/system-logs/SystemLogsView";
import { AccountSecuritySettings, AccountSettingsDialog } from "@/features/access/AccountSettingsDialog";
import { PlanSettingsDialog, type PlanSettingsTab } from "@/features/access/PlanSettingsDialog";
import { canWorkspace, useEffectiveWorkspaceRole } from "@/features/access/workspace-access";
import { workspaceDangerTab } from "@/features/access/workspace-danger";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { ShortcutsBody } from "@/features/help/ShortcutsDialog";
import { AppearanceSettings, CalendarSettings, ScheduleSettings, ViewSettings } from "@/features/settings/SettingsDialog";
import { PlannerSettingsBody } from "@/features/tasks/PlannerSettingsDialog";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import {
  settingsScopeLabel,
  settingsSectionLabel,
  settingsSections,
  settingsSurface,
  settingsSurfaceScopes,
  settingsTrail,
  useSettingsHub,
  type SettingsScope,
  type SettingsSection,
} from "./settings-hub";
import { WorkspaceSettingsPane } from "./WorkspaceSwitcher";

const useStyles = makeStyles({
  shell: {
    display: "flex",
    flex: "1 1 auto",
    minHeight: 0,
    height: "100%",
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralBackground1,
    "@media (max-width: 760px)": {
      flexDirection: "column",
    },
  },
  /* 앱 사이드와 같은 220px. 면은 흰색. 구분선은 너비를 먹지 않게 안쪽 그림자로 둔다. */
  nav: {
    width: "220px",
    boxSizing: "border-box",
    flexShrink: 0,
    minHeight: 0,
    overflow: "auto",
    padding: "4px 8px",
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: `inset -1px 0 0 ${tokens.colorNeutralStroke2}`,
    "@media (max-width: 760px)": {
      width: "100%",
      maxHeight: "180px",
      boxShadow: `inset 0 -1px 0 ${tokens.colorNeutralStroke2}`,
    },
  },
  group: {
    display: "flex",
    flexDirection: "column",
  },
  groupRule: {
    flexShrink: 0,
    height: "1px",
    marginTop: "8px",
    marginBottom: "6px",
    marginLeft: "6px",
    marginRight: "6px",
    backgroundImage: `repeating-linear-gradient(to right, color-mix(in srgb, ${tokens.colorNeutralForeground3} 45%, transparent) 0 1px, transparent 1px 4px)`,
  },
  groupLabel: {
    display: "flex",
    alignItems: "center",
    height: "24px",
    marginTop: 0,
    marginBottom: "2px",
    marginLeft: 0,
    marginRight: 0,
    padding: "0 6px",
    fontSize: "11px",
    lineHeight: "14px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  items: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  /* 앱 사이드 항목과 같은 줄. 28px, 14px 레귤러, 선택은 --nav-on. */
  item: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    height: "28px",
    minHeight: "28px",
    maxHeight: "28px",
    boxSizing: "border-box",
    padding: "0 8px",
    border: "none",
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: "transparent",
    color: "var(--nav-ink)",
    cursor: "pointer",
    textAlign: "left",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0",
    fontWeight: tokens.fontWeightRegular,
    flexShrink: 0,
    transitionProperty: "background-color, color",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Selected,
      color: "var(--nav-ink)",
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  itemOn: {
    backgroundColor: "var(--nav-on)",
    color: "var(--nav-ink-on)",
    ":hover": {
      backgroundColor: "var(--nav-on)",
      color: "var(--nav-ink-on)",
    },
    ":active": {
      backgroundColor: "var(--nav-on)",
    },
    "@media (forced-colors: active)": {
      outline: "2px solid Highlight",
    },
  },
  icon: {
    display: "inline-flex",
    flexShrink: 0,
    width: "18px",
    height: "18px",
    color: "inherit",
    "& svg": {
      width: "18px",
      height: "18px",
    },
  },
  itemLabel: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  content: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: "20px 28px 0",
  },
  contentHead: {
    flexShrink: 0,
    marginBottom: "16px",
    paddingRight: "16px",
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    paddingBottom: "24px",
  },
  scrollFill: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    paddingBottom: "0",
  },
  fill: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
});

const SECTION_ICON: Record<string, { icon: FluentIcon; iconOn: FluentIcon }> = {
  appearance: { icon: Color20Regular, iconOn: Color20Filled },
  account: { icon: Person20Regular, iconOn: Person20Filled },
  view: { icon: Eye20Regular, iconOn: Eye20Filled },
  shortcuts: { icon: Keyboard20Regular, iconOn: Keyboard20Filled },
  integration: { icon: PlugConnected20Regular, iconOn: PlugConnected20Filled },
  logs: { icon: DocumentBulletList20Regular, iconOn: DocumentBulletList20Filled },
  general: { icon: Settings20Regular, iconOn: Settings20Filled },
  invites: { icon: PersonAdd20Regular, iconOn: PersonAdd20Filled },
  access: { icon: Key20Regular, iconOn: Key20Filled },
  schedule: { icon: CalendarLtr20Regular, iconOn: CalendarLtr20Filled },
  calendar: { icon: Calendar20Regular, iconOn: Calendar20Filled },
  buckets: { icon: Tag20Regular, iconOn: Tag20Filled },
  members: { icon: People20Regular, iconOn: People20Filled },
  security: { icon: Shield20Regular, iconOn: Shield20Filled },
  share: { icon: Share20Regular, iconOn: Share20Filled },
  audit: { icon: History20Regular, iconOn: History20Filled },
  danger: { icon: Warning20Regular, iconOn: Warning20Filled },
};

function sectionIcon(scope: SettingsScope, id: string): { icon: FluentIcon; iconOn: FluentIcon } {
  if (scope === "workspace" && id === "general") return { icon: Building20Regular, iconOn: Building20Filled };
  if (scope === "plan" && id === "general") return { icon: Board20Regular, iconOn: Board20Filled };
  return SECTION_ICON[id] ?? { icon: Settings20Regular, iconOn: Settings20Filled };
}

type NavFlags = { invite: boolean; audit: boolean; danger: boolean; schedule: boolean; buckets: boolean; plan: boolean };

function visibleSections(scope: SettingsScope, flags: NavFlags): SettingsSection[] {
  if (scope === "plan" && !flags.plan) return [];
  return settingsSections(scope).filter((item) => {
    if (scope === "workspace") {
      if ((item.id === "invites" || item.id === "access") && !flags.invite) return false;
      if (item.id === "audit" && !flags.audit) return false;
      if (item.id === "danger" && !flags.danger) return false;
    }
    if (scope === "plan") {
      if ((item.id === "schedule" || item.id === "calendar") && !flags.schedule) return false;
      if (item.id === "buckets" && !flags.buckets) return false;
    }
    return true;
  });
}

function navItems(flags: NavFlags, scope: SettingsScope | null): Array<SettingsSection & { scope: SettingsScope }> {
  return settingsSurfaceScopes(settingsSurface(scope ?? "app")).flatMap((group) =>
    visibleSections(group, flags).map((item) => ({ ...item, scope: group })),
  );
}

export function SettingsModal() {
  const styles = useStyles();
  const scope = useSettingsHub((s) => s.scope);
  const section = useSettingsHub((s) => s.section);
  const planName = useProjectStore((s) => s.project.name);
  const activeId = useProjectStore((s) => s.activeId);
  const workspaceName = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]?.name ?? "");
  const role = useEffectiveWorkspaceRole();
  const surface = usePlanSurface();
  const navRef = useRef<HTMLElement | null>(null);
  const [navNode, setNavNode] = useState<HTMLElement | null>(null);
  const setNav = useCallback((node: HTMLElement | null) => {
    navRef.current = node;
    setNavNode(node);
  }, []);
  const contentRef = useRef<HTMLDivElement>(null);
  const opened = useRef(false);

  const flags: NavFlags = {
    invite: canWorkspace(role, "member.invite"),
    audit: canWorkspace(role, "audit.read"),
    danger: workspaceDangerTab(role),
    schedule: surface.editSchedule,
    buckets: surface.editWbs,
    plan: !!activeId,
  };
  const items = navItems(flags, scope);
  const itemKey = items.map((item) => `${item.scope}:${item.id}`).join("|");
  const names = { workspace: workspaceName, plan: planName };
  const planSurface = scope === "plan";

  useEffect(() => {
    if (scope === "plan" && !activeId) useSettingsHub.getState().forceClose();
  }, [scope, activeId]);

  useEffect(() => {
    if (scope) return;
    opened.current = false;
  }, [scope]);

  useEffect(() => {
    opened.current = false;
  }, [planSurface]);

  useEffect(() => {
    if (!scope) return;
    const keys = itemKey.split("|").filter(Boolean);
    if (keys.includes(`${scope}:${section}`)) return;
    const [nextScope, nextSection] = (keys[0] ?? "").split(":");
    if (!nextScope || !nextSection) return;
    useSettingsHub.getState().setSection(nextSection, nextScope as SettingsScope);
  }, [scope, section, itemKey]);

  useEffect(() => {
    const nav = navNode;
    if (!scope || !nav) return;
    const button = nav.querySelector<HTMLButtonElement>("[aria-current='page']");
    if (!button) return;
    if (!opened.current) {
      opened.current = true;
      button.focus({ preventScroll: true });
    }
    const navRect = nav.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const pad = 28;
    if (buttonRect.top < navRect.top + pad) nav.scrollTop -= navRect.top + pad - buttonRect.top;
    else if (buttonRect.bottom > navRect.bottom) nav.scrollTop += buttonRect.bottom - navRect.bottom;
  }, [scope, section, navNode]);

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [scope, section]);

  if (!scope || (scope === "plan" && !activeId)) return null;

  const dialogTitle = planSurface ? (planName.trim() || "플랜 설정") : "설정";
  const navLabel = planSurface ? "플랜 설정" : "설정";

  const planTab = (["general", "members", "security", "share", "audit"] as const).includes(section as PlanSettingsTab)
    ? (section as PlanSettingsTab)
    : null;
  const fill = scope === "app" && (section === "logs" || section === "integration");
  const bucketsPage = scope === "plan" && section === "buckets" && !!activeId;

  const select = (nextScope: SettingsScope, nextSection: string) => {
    const before = useSettingsHub.getState();
    before.setSection(nextSection, nextScope);
    const after = useSettingsHub.getState();
    if (after.scope === nextScope && after.section === nextSection) return;
    navRef.current?.querySelector<HTMLButtonElement>("[aria-current='page']")?.focus();
  };

  const onNavKey = (event: KeyboardEvent<HTMLElement>) => {
    const buttons = [...(navRef.current?.querySelectorAll<HTMLButtonElement>("[data-settings-item]") ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (index < 0) return;
    let next = -1;
    if (event.key === "ArrowDown") next = Math.min(buttons.length - 1, index + 1);
    else if (event.key === "ArrowUp") next = Math.max(0, index - 1);
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = buttons.length - 1;
    else return;
    event.preventDefault();
    buttons[next]?.focus();
    buttons[next]?.click();
  };

  const sectionBody = (
    <>
      {scope === "app" && section === "appearance" ? <AppearanceSettings /> : null}
      {scope === "app" && section === "account" ? <AccountSettingsDialog open embedded onClose={() => undefined} /> : null}
      {scope === "app" && section === "security" ? <AccountSecuritySettings onSignedOut={() => useSettingsHub.getState().close()} /> : null}
      {scope === "app" && section === "view" ? <ViewSettings /> : null}
      {scope === "app" && section === "shortcuts" ? <ShortcutsBody /> : null}
      {scope === "app" && section === "integration" ? <McpConnectionView /> : null}
      {scope === "app" && section === "logs" ? <SystemLogsView /> : null}
      {scope === "workspace" ? <WorkspaceSettingsPane section={section} /> : null}
      {scope === "plan" && section === "schedule" && activeId ? <ScheduleSettings /> : null}
      {scope === "plan" && section === "calendar" && activeId ? <CalendarSettings /> : null}
      {bucketsPage ? <PlannerSettingsBody /> : null}
      {scope === "plan" && planTab && activeId ? (
        <PlanSettingsDialog
          embedded
          open
          planName={planName}
          section={planTab}
          onSection={(next) => useSettingsHub.getState().setSection(next, "plan")}
          onClose={() => useSettingsHub.getState().forceClose()}
        />
      ) : null}
    </>
  );

  return (
    <AppDialog
      open
      onClose={() => useSettingsHub.getState().close()}
      title={dialogTitle}
      width="xl"
      height={640}
      chrome="corner"
      className="w-[min(1000px,calc(100vw-2rem))] max-w-[min(1000px,calc(100vw-2rem))] sm:max-w-[min(1000px,calc(100vw-2rem))] max-h-[calc(100dvh-48px)] p-0"
    >
      <div className={styles.shell}>
        <nav ref={setNav} className={styles.nav} aria-label={navLabel} onKeyDown={onNavKey}>
          {settingsSurfaceScopes(settingsSurface(scope)).flatMap((group) => {
            const groupItems = items.filter((item) => item.scope === group);
            if (groupItems.length === 0) return [];
            return [{ group, groupItems, label: settingsScopeLabel(group, names) }];
          }).map((entry, index) => (
            <div key={entry.group} className={styles.group}>
              {index > 0 ? <div className={styles.groupRule} aria-hidden /> : null}
              <p className={styles.groupLabel} title={entry.label}>{entry.label}</p>
              <div className={styles.items}>
                {entry.groupItems.map((item) => {
                  const on = item.scope === scope && item.id === section;
                  const icons = sectionIcon(item.scope, item.id);
                  const Icon = on ? icons.iconOn : icons.icon;
                  return (
                    <button
                      key={`${item.scope}:${item.id}`}
                      type="button"
                      className={mergeClasses(styles.item, on && styles.itemOn)}
                      data-settings-item=""
                      aria-current={on ? "page" : undefined}
                      title={`${entry.label} · ${item.label}`}
                      onClick={() => select(item.scope, item.id)}
                    >
                      <span className={styles.icon} aria-hidden>
                        <Icon />
                      </span>
                      <span className={styles.itemLabel}>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className={styles.content}>
          <PageHeaderTitle className={styles.contentHead}>{settingsTrail(scope, section)}</PageHeaderTitle>
          <div
            ref={contentRef}
            className={mergeClasses(styles.scroll, fill && styles.scrollFill)}
            aria-label={settingsSectionLabel(scope, section)}
          >
            {fill ? <div className={styles.fill}>{sectionBody}</div> : sectionBody}
          </div>
        </div>
      </div>
    </AppDialog>
  );
}
